import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

interface RevealProps {
  children: ReactNode;
  /** Stagger delay in milliseconds (converted to seconds for framer-motion). */
  delayMs?: number;
  className?: string;
}

/**
 * Scroll-reveal wrapper for the SaaS marketing pages. Fades + slides content up
 * the first time it enters the viewport. Encapsulates the motion choice so the
 * pages stay declarative.
 *
 * Accessibility: when the user prefers reduced motion we render a plain <div>
 * with no animation (content visible immediately) — this also keeps the content
 * painted under SSR / no-JS / JSDOM, where IntersectionObserver-driven reveals
 * could otherwise strand it at opacity:0.
 */
export default function Reveal({ children, delayMs = 0, className }: RevealProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: delayMs / 1000 }}
    >
      {children}
    </motion.div>
  );
}
